import {
  AlertCircle,
  Fuel,
  Loader2,
  Minus,
  Pencil,
  Plus,
  Search,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LocationAutocomplete } from "@/components/common/PlaceAutocompleteInput";
import { FareCalculationExplanation } from "@/components/dailyCommute/FareCalculationExplanation";
import { VehicleFareResults } from "@/components/dailyCommute/VehicleFareResults";
import { RouteSummaryCard } from "@/components/dailyCommute/RouteSummaryCard";
import {
  FUEL_TYPES,
  MIN_SEATS,
  MIN_AVAILABLE_SEATS,
  MAX_AVAILABLE_SEATS,
  maxOfferingSeats,
} from "@/components/dailyCommute/commuteCalculatorConstants";
import type { useCommuteCalculator } from "@/hooks/useCommuteCalculator";
import { useRouteEstimate } from "@/hooks/useRouteEstimate";
import type { FuelType } from "@/types/fareCalculation";
import { cn } from "@/lib/utils";

type CommuteCalculatorState = ReturnType<typeof useCommuteCalculator>;

interface CommuteCalculatorContentProps {
  calculator: CommuteCalculatorState;
  idPrefix?: string;
  className?: string;
}

function SelectLoadingState({ message = "Loading..." }: { message?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {message}
    </div>
  );
}

interface SeatStepperProps {
  id: string;
  label: string;
  value: number | null;
  placeholder?: string;
  onDecrement: () => void;
  onIncrement: () => void;
  disableDecrement?: boolean;
  disableIncrement?: boolean;
}

function SeatStepper({
  id,
  label,
  value,
  placeholder = "—",
  onDecrement,
  onIncrement,
  disableDecrement,
  disableIncrement,
}: SeatStepperProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold">
        {label}
      </Label>
      <div
        id={id}
        className="flex h-11 items-center justify-between rounded-md border border-input bg-background px-1.5"
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          disabled={disableDecrement}
          onClick={onDecrement}
          aria-label={`Decrease ${label.toLowerCase()}`}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span
          className={cn(
            "min-w-[2ch] text-center text-base font-semibold tabular-nums",
            value == null && "text-muted-foreground"
          )}
        >
          {value ?? placeholder}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          disabled={disableIncrement}
          onClick={onIncrement}
          aria-label={`Increase ${label.toLowerCase()}`}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function CommuteCalculatorContent({
  calculator,
  idPrefix = "commute",
  className,
}: CommuteCalculatorContentProps) {
  const {
    fromPlace,
    toPlace,
    destinationKey,
    selectedMakeId,
    selectedModelId,
    fuelType,
    seats,
    availableSeats,
    fareResult,
    selectedFare,
    setSelectedFare,
    showExplanation,
    setShowExplanation,
    validationError,
    isDetectingLocation,
    showFareResults,
    makes,
    makesLoading,
    makesError,
    models,
    modelsLoading,
    modelsError,
    selectedMake,
    selectedModel,
    fromFullAddress,
    toFullAddress,
    fareMutation,
    fareExplanationData,
    clearFareResult,
    clearValidationError,
    handleFromPlaceSelect,
    handleToPlaceSelect,
    handleClearFrom,
    handleClearTo,
    handleMakeChange,
    handleModelChange,
    handleFuelTypeChange,
    decrementSeats,
    incrementSeats,
    decrementAvailableSeats,
    incrementAvailableSeats,
    handleSearchFare,
  } = calculator;

  const hasRoute = Boolean(fromFullAddress.trim() && toFullAddress.trim());
  const {
    data: routeEstimate,
    isLoading: routeEstimateLoading,
  } = useRouteEstimate(fromPlace, toPlace, hasRoute);

  return (
    <>
      <div className={cn("commute-flip-scene", className)}>
        <div
          className={cn("commute-flip-card", showFareResults && "is-flipped")}
          aria-live="polite"
        >
          {/* Front: trip form */}
          <div className="commute-flip-face commute-flip-face--front space-y-5 sm:space-y-6">
            <div className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <LocationAutocomplete
                  id={`${idPrefix}-from`}
                  label="From (Home / Pickup)"
                  placeholder={
                    isDetectingLocation
                      ? "Detecting your current location..."
                      : "Search starting location"
                  }
                  mode="origin"
                  value={fromPlace}
                  isLoading={isDetectingLocation}
                  disabled={isDetectingLocation}
                  onPlaceSelect={handleFromPlaceSelect}
                  onClear={handleClearFrom}
                  onInteraction={clearValidationError}
                />
                <LocationAutocomplete
                  key={`${idPrefix}-to-${destinationKey}`}
                  id={`${idPrefix}-to`}
                  label="To (Office / Destination)"
                  placeholder="Search destination"
                  mode="destination"
                  value={toPlace}
                  restrictToCountryCode={fromPlace?.countryCode}
                  onPlaceSelect={handleToPlaceSelect}
                  onClear={handleClearTo}
                  onInteraction={clearValidationError}
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`${idPrefix}-make`} className="text-sm font-semibold">
                    Vehicle Make
                  </Label>
                  <Select value={selectedMakeId} onValueChange={handleMakeChange}>
                    <SelectTrigger id={`${idPrefix}-make`} className="h-11">
                      <SelectValue placeholder="Select make" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {makesLoading && <SelectLoadingState />}
                      {makesError && (
                        <div className="px-3 py-4 text-sm text-destructive">
                          Failed to load vehicle makes.
                        </div>
                      )}
                      {!makesLoading && !makesError && makes.length === 0 && (
                        <div className="px-3 py-4 text-sm text-muted-foreground">
                          No makes found.
                        </div>
                      )}
                      {!makesLoading &&
                        !makesError &&
                        makes.map((make) => (
                          <SelectItem key={make.maId} value={make.maId}>
                            {make.maNa}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`${idPrefix}-model`} className="text-sm font-semibold">
                    Vehicle Model
                  </Label>
                  <Select
                    value={selectedModelId}
                    onValueChange={handleModelChange}
                    disabled={!selectedMakeId}
                  >
                    <SelectTrigger id={`${idPrefix}-model`} className="h-11">
                      <SelectValue
                        placeholder={selectedMakeId ? "Select model" : "Select a make first"}
                      />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {modelsLoading && <SelectLoadingState />}
                      {modelsError && (
                        <div className="px-3 py-4 text-sm text-destructive">
                          Failed to load vehicle models.
                        </div>
                      )}
                      {!modelsLoading &&
                        !modelsError &&
                        models.length === 0 &&
                        selectedMakeId && (
                          <div className="px-3 py-4 text-sm text-muted-foreground">
                            No models found.
                          </div>
                        )}
                      {!modelsLoading &&
                        !modelsError &&
                        models.map((model) => (
                          <SelectItem key={model.moId} value={model.moId}>
                            {model.moNa}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor={`${idPrefix}-fuel`} className="text-sm font-semibold">
                    Fuel Type
                  </Label>
                  <Select
                    value={fuelType}
                    onValueChange={(value) => handleFuelTypeChange(value as FuelType)}
                  >
                    <SelectTrigger id={`${idPrefix}-fuel`} className="h-11">
                      <SelectValue placeholder="Select fuel type" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {FUEL_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          <span className="flex items-center gap-2">
                            <Fuel className="h-3.5 w-3.5" />
                            {type}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <SeatStepper
                  id={`${idPrefix}-available-seats`}
                  label="Available Seats"
                  value={availableSeats}
                  placeholder="Set"
                  onDecrement={decrementAvailableSeats}
                  onIncrement={incrementAvailableSeats}
                  disableDecrement={
                    availableSeats == null || availableSeats <= MIN_AVAILABLE_SEATS
                  }
                  disableIncrement={(availableSeats ?? 0) >= MAX_AVAILABLE_SEATS}
                />

                <SeatStepper
                  id={`${idPrefix}-offering-seats`}
                  label="Offering Seats"
                  value={seats}
                  onDecrement={decrementSeats}
                  onIncrement={incrementSeats}
                  disableDecrement={seats <= MIN_SEATS}
                  disableIncrement={seats >= maxOfferingSeats(availableSeats)}
                />
              </div>

              {validationError && (
                <Alert variant="destructive" className="border-destructive/30">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm leading-relaxed">
                    {validationError}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="button"
                size="lg"
                className="h-12 w-full gap-2 bg-primary text-base font-semibold text-primary-foreground shadow-md hover:bg-primary/90"
                disabled={fareMutation.isPending}
                onClick={handleSearchFare}
              >
                {fareMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Calculating fare...
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    Check your savings now
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Back: fare details */}
          <div className="commute-flip-face commute-flip-face--back space-y-6">
            <Button
              type="button"
              variant="outline"
              className="h-10 gap-2 border-border/80 text-sm font-semibold shadow-sm"
              onClick={clearFareResult}
            >
              <Pencil className="h-4 w-4" />
              Edit details
            </Button>

            {showFareResults && fareResult && (
              <>
                <RouteSummaryCard
                  fromAddress={fromFullAddress}
                  toAddress={toFullAddress}
                  make={selectedMake?.maNa}
                  model={selectedModel?.moNa}
                  fuelType={fuelType}
                  seats={seats}
                  routeEstimate={routeEstimate}
                  routeEstimateLoading={routeEstimateLoading}
                />

                <VehicleFareResults
                  fareCalculation={fareResult.fareCalculation}
                  selectedFare={selectedFare}
                  onSelectedFareChange={setSelectedFare}
                  seats={seats}
                  onHowCalculatedClick={() => setShowExplanation(true)}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {fareExplanationData && (
        <FareCalculationExplanation
          open={showExplanation}
          onClose={() => setShowExplanation(false)}
          fareData={fareExplanationData}
          fareCalculationData={calculator.fareResult?.fareCalculation ?? null}
        />
      )}
    </>
  );
}
